/**
 * CodeViewer Component
 *
 * Displays code files with syntax highlighting using highlight.js.
 * Supports line numbers, scrolling, and various programming languages.
 */

import { useMemo, useEffect, useRef } from 'react';
import hljs from 'highlight.js';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

interface CodeViewerProps {
  /** The code content to display */
  content: string;
  /** File name (used for language detection) */
  filename: string;
  /** Optional language override */
  language?: string;
  /** Show line numbers (default: true) */
  showLineNumbers?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ============================================
// Language Detection
// ============================================

/**
 * Map file extensions to highlight.js language names
 */
const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  // JavaScript/TypeScript
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  mjs: 'javascript',
  cjs: 'javascript',

  // Python
  py: 'python',
  pyw: 'python',
  pyi: 'python',

  // Rust
  rs: 'rust',

  // Go
  go: 'go',

  // Java
  java: 'java',

  // C/C++
  c: 'c',
  h: 'c',
  cpp: 'cpp',
  cc: 'cpp',
  cxx: 'cpp',
  hpp: 'cpp',
  hh: 'cpp',
  hxx: 'cpp',

  // C#
  cs: 'csharp',

  // Ruby
  rb: 'ruby',

  // PHP
  php: 'php',

  // Swift
  swift: 'swift',

  // Kotlin
  kt: 'kotlin',
  kts: 'kotlin',

  // Scala
  scala: 'scala',

  // Web
  html: 'html',
  htm: 'html',
  css: 'css',
  scss: 'scss',
  sass: 'scss',
  less: 'less',

  // Data/Config
  json: 'json',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  toml: 'ini',
  ini: 'ini',
  conf: 'ini',
  cfg: 'ini',

  // Shell
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  fish: 'bash',
  ps1: 'powershell',

  // Database
  sql: 'sql',

  // GraphQL
  graphql: 'graphql',
  gql: 'graphql',

  // Markdown
  md: 'markdown',
  markdown: 'markdown',

  // Docker
  dockerfile: 'dockerfile',

  // Makefile
  makefile: 'makefile',
  mk: 'makefile',

  // Lua
  lua: 'lua',

  // Perl
  pl: 'perl',
  pm: 'perl',

  // R
  r: 'r',

  // Vim
  vim: 'vim',

  // Diff
  diff: 'diff',
  patch: 'diff',
};

/**
 * Detect language from filename
 */
function detectLanguage(filename: string): string | undefined {
  // Handle special files without extensions
  const lowerFilename = filename.toLowerCase();
  const specialFiles: Record<string, string> = {
    dockerfile: 'dockerfile',
    makefile: 'makefile',
    'gnumakefile': 'makefile',
    gitignore: 'bash',
    '.gitignore': 'bash',
    '.env': 'bash',
    '.envrc': 'bash',
    '.bashrc': 'bash',
    '.zshrc': 'bash',
    '.profile': 'bash',
  };

  if (specialFiles[lowerFilename]) {
    return specialFiles[lowerFilename];
  }

  // Extract extension
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return undefined;

  const ext = filename.slice(lastDot + 1).toLowerCase();
  return EXTENSION_TO_LANGUAGE[ext];
}

// ============================================
// CodeViewer Component
// ============================================

export function CodeViewer({
  content,
  filename,
  language,
  showLineNumbers = true,
  className,
}: CodeViewerProps) {
  const codeRef = useRef<HTMLElement>(null);

  // Detect language from filename if not provided
  const detectedLanguage = useMemo(() => {
    if (language) return language;
    return detectLanguage(filename);
  }, [filename, language]);

  // Highlight the code
  const highlightedCode = useMemo(() => {
    if (!content) return '';

    try {
      if (detectedLanguage && hljs.getLanguage(detectedLanguage)) {
        const result = hljs.highlight(content, { language: detectedLanguage });
        return result.value;
      } else {
        // Auto-detect language
        const result = hljs.highlightAuto(content);
        return result.value;
      }
    } catch {
      // Fallback to escaped plain text
      return escapeHtml(content);
    }
  }, [content, detectedLanguage]);

  // Split into lines for line numbers
  const lines = useMemo(() => {
    // Split by newlines but preserve the HTML structure
    // We need to be careful with multi-line spans
    return highlightedCode.split('\n');
  }, [highlightedCode]);

  // Re-highlight when content changes (for dynamic updates)
  useEffect(() => {
    if (codeRef.current) {
      // Clear previous highlighting data attribute
      delete codeRef.current.dataset.highlighted;
    }
  }, [content]);

  return (
    <div
      className={cn(
        'h-full w-full overflow-auto',
        'bg-surface-950 dark:bg-surface-950',
        'font-mono text-sm',
        className
      )}
    >
      <div className="min-w-max">
        <table className="w-full border-collapse">
          <tbody>
            {lines.map((line, index) => (
              <tr key={index} className="hover:bg-surface-900/50 transition-colors">
                {showLineNumbers && (
                  <td
                    className={cn(
                      'sticky left-0 select-none text-right',
                      'px-4 py-0 leading-6',
                      'text-surface-500 bg-surface-950',
                      'border-r border-surface-800',
                      'w-12 min-w-12'
                    )}
                  >
                    <span className="text-xs">{index + 1}</span>
                  </td>
                )}
                <td className="px-4 py-0 leading-6">
                  <code
                    ref={index === 0 ? codeRef : undefined}
                    className="text-surface-100"
                    dangerouslySetInnerHTML={{ __html: line || '&nbsp;' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

export default CodeViewer;
